"use client";

import { Box, Container, Grid, Typography } from "@mui/material";
import React, { FC } from "react";
import SalifyFallback from "src/lib/components/salify-fallback/default";
import DynamicCard from "src/lib/components/dynamic-card/default";
import { DynamicCardBlocks, CardTheme } from "src/lib/components/dynamic-card/types";

// PET Machine Product Interface
interface PETMachine {
  id: string;
  name: string;
  image: string;
  shortDescription: string;
  specifications: string[];
  link: string;
}

export interface VariantProps {
  title?: string;
  subtitle?: string;
  theme?: "default" | "haianh" | "bazaar" | "footmart" | "nova-press";
  containerMaxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  showBorder?: boolean;
  showBoxShadow?: boolean;
  sectionId?: string;
}

// PET Manufacturing Machines Data
const petMachines: PETMachine[] = [
  {
    id: "pet-lightning",
    name: "Máy ép phôi PET dòng Lightning",
    image: "/pet-ligtning.png",
    shortDescription: "Máy ép phôi PET hiệu suất cao với hệ thống Direct-Clamping tiên tiến",
    specifications: [
      "For 96, 128, 144 Cavities",
      "Cycle: 7S/14g/128cav",
      "Direct-Clamping System",
      "3 Station Cooling Robot",
    ],
    link: "/product/pet-lightning",
  },
  {
    id: "pet-eco",
    name: "Máy ép phôi PET ECO",
    image: "/pet-eco.png",
    shortDescription: "Giải pháp kinh tế với khả năng ép đồng thời áp suất và dẻo hóa",
    specifications: [
      "For 24, 32, 48, 72 Cavities",
      "Cycle: 8.5S/14g",
      "Pressure and Plasticization simultaneously",
      "3 Station Cooling Robot",
    ],
    link: "/product/pet-eco",
  },
  {
    id: "pet-line-s",
    name: "Máy tạo phôi PET dòng S",
    image: "/pet-line-s.png",
    shortDescription: "Máy ép phôi đầu tư thấp, phù hợp cho doanh nghiệp vừa và nhỏ",
    specifications: [
      "Low investment",
      "For 16, 24, 32 Cavities",
      "Cycle: 14S/14g/32cav",
      "Without Cooling Robot",
    ],
    link: "/product/pet-line-s",
  },
  {
    id: "pet-ipet",
    name: "Hệ thống ép phôi PET iPET",
    image: "/pet-ipet.png",
    shortDescription: "Hệ thống iPET tiên tiến với công nghệ Two-Step Injection",
    specifications: [
      "For 72, 96 Cavities",
      "Cycle: 8S/14g/72cav",
      "Two-Step Injection",
      "5 sets of high efficient accumulator",
      "3 Station Cooling Robot",
    ],
    link: "/product/pet-ipet",
  },
];

// Map theme variants to supported CardTheme values
const mapThemeToCardTheme = (theme: VariantProps["theme"]): CardTheme => {
  switch (theme) {
    case "haianh":
      return "haianh";
    case "nova-press":
      return "nova-press";
    case "bazaar":
    case "footmart":
    case "default":
    default:
      return "default";
  }
};

const PETProductsGrid: FC<VariantProps> = ({
  title = "Các dòng máy ép phôi PET",
  subtitle = "Khám phá dòng sản phẩm máy ép phôi PET chất lượng cao của Hải Anh",
  theme = "default",
  containerMaxWidth = "lg",
  showBorder = true,
  showBoxShadow = true,
  sectionId,
}) => {
  // Convert PET machine to dynamic card blocks
  const createMachineBlocks = (machine: PETMachine): DynamicCardBlocks => ({
    image: {
      src: machine.image,
      alt: machine.name,
      style: {
        width: "100%",
        height: "280px",
        objectFit: "cover" as const,
      },
      wrapperStyle: {
        padding: "16px",
        paddingBottom: 0,
      },
    },
    header: {
      title: {
        text: machine.name,
        cssProps: {
          variant: "h6" as const,
          fontWeight: 700,
          color: "#333",
          sx: {
            fontSize: "1.25rem",
            lineHeight: 1.3,
            marginBottom: "12px",
            minHeight: "60px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          },
        },
      },
    },
    content: {
      description: {
        text: machine.shortDescription,
        cssProps: {
          variant: "body2" as const,
          color: "#666",
          sx: {
            fontSize: "0.875rem",
            lineHeight: 1.5,
            marginBottom: "16px",
            minHeight: "40px",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          },
        },
      },
      bulletPoints: machine.specifications.map((spec) => ({
        icon: "•",
        text: spec,
        cssProps: {
          color: "#555",
          fontSize: "0.8rem",
        },
      })),
    },
    actions: {
      buttons: [
        {
          type: "primary",
          text: "Xem chi tiết",
          href: machine.link,
          style: {
            fontSize: "0.875rem",
            fontWeight: 600,
            backgroundColor: theme === "haianh" ? "#1e3c72" : "#2196F3",
            color: "white",
            padding: "8px 24px",
            borderRadius: "6px",
            textDecoration: "none",
          },
        },
      ],
    },
  });

  return (
    <Box
      id={sectionId}
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: "#f8f9fa",
      }}
    >
      <Container maxWidth={containerMaxWidth}>
        {/* Section Header */}
        <Box textAlign="center" mb={6}>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              color: theme === "haianh" ? "#1e3c72" : "#333",
              fontWeight: 700,
              fontSize: { xs: "2rem", md: "2.5rem" },
              letterSpacing: "0.02em",
              mb: 2,
            }}
          >
            <SalifyFallback.Label path="title" default={title}>
              {title}
            </SalifyFallback.Label>
          </Typography>
          {subtitle && (
            <Typography
              variant="h6"
              component="p"
              sx={{
                color: "#666",
                fontWeight: 400,
                fontSize: { xs: "1rem", md: "1.2rem" },
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              <SalifyFallback.Label path="subtitle" default={subtitle}>
                {subtitle}
              </SalifyFallback.Label>
            </Typography>
          )}
        </Box>

        {/* Products Grid */}
        <Grid container spacing={{ xs: 3, md: 4 }}>
          {petMachines.map((machine, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={6}
              key={machine.id}
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <DynamicCard
                variant="vertical"
                imagePosition="top"
                spacing="normal"
                theme={mapThemeToCardTheme(theme)}
                blocks={createMachineBlocks(machine)}
                link={machine.link}
                showBorder={showBorder}
                showBoxShadow={showBoxShadow}
                containerProps={{
                  sx: {
                    height: "100%",
                    maxWidth: "450px",
                    width: "100%",
                    backgroundColor: "#fff",
                    borderRadius: "12px",
                    overflow: "hidden",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 12px 32px rgba(0, 0, 0, 0.15)",
                    },
                  },
                }}
              />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

PETProductsGrid.displayName = "PETProductsGrid";

export default PETProductsGrid;
